import type { ColorScheme } from '@mantine/core';
import { Box } from '@mantine/core';
import type { IWidget, MetricName } from '../../api';
import { WidgetSize } from '../../interfaces';
import { WIDGET_SIZE_MAP } from '../AnalyticsPage/constants';
import { getWidgetSpan } from '../AnalyticsPage/utils/utils';
import { useStyles } from './AnalyticsListPage.styles';
import dqs from './assets/dqs.png';
import dqs_dark from './assets/dqs_dark.png';
import empty from './assets/empty.png';
import empty_dark from './assets/empty_dark.png';
import lineInfo from './assets/lineInfo.png';
import lineInfo_dark from './assets/lineInfo_dark.png';
import lineSuccess from './assets/lineSuccess.png';
import lineSuccess_dark from './assets/lineSuccess_dark.png';
import lineWarning from './assets/lineWarning.png';
import lineWarning_dark from './assets/lineWarning_dark.png';
import list from './assets/list.png';
import list_dark from './assets/list_dark.png';
import numeric from './assets/numeric.png';
import numeric_dark from './assets/numeric_dark.png';

const IMAGES: Record<string, Record<ColorScheme, string>> = {
	lineInfo: { light: lineInfo, dark: lineInfo_dark },
	lineSuccess: { light: lineSuccess, dark: lineSuccess_dark },
	lineWarning: { light: lineWarning, dark: lineWarning_dark },
	list: { light: list, dark: list_dark },
	numeric: { light: numeric, dark: numeric_dark },
	dqs: { light: dqs, dark: dqs_dark },
	empty: { light: empty, dark: empty_dark },
};

const IMAGE_MAP: Record<MetricName, string> = {
	DAILY_ACTIVE_USER_COUNT: 'lineInfo',
	WEEKLY_ACTIVE_USER_COUNT: 'lineInfo',
	MONTHLY_ACTIVE_USER_COUNT: 'lineInfo',
	RESOURCE_COUNT: 'lineInfo',
	DAILY_RESOURCE_VIEW_COUNT: 'lineInfo',
	DAILY_SEARCH_COUNT: 'lineInfo',
	TOP_RESOURCES: 'list',
	TOP_SEARCHES: 'list',
	TOP_USERS: 'list',
	TOP_RESOURCE_OWNERS: 'list',
	LEAST_ACTIVE_EDITORS: 'list',
	TOP_USER_SEARCHES: 'list',
	TOP_GROUPS: 'list',
	TOP_GROUPS_BY_ENTITIES: 'list',
	PERCENTAGE_DOCUMENTED_RESOURCES: 'lineInfo',
	QUESTIONS_ASKED_COUNT: 'lineInfo',
	TIME_QUESTION_AVERAGE: 'lineInfo',
	TOP_QUERIED_TABLES: 'list',
	TOP_TAGS: 'list',
	PERCENTAGE_VERIFIED_RESOURCES: 'lineInfo',

	SNOWFLAKE_COST: 'lineInfo',
	SNOWFLAKE_CREDITS_PER_WAREHOUSE: 'lineInfo',
	SNOWFLAKE_CREDITS_PER_USER: 'lineInfo',
	SNOWFLAKE_QUERY_VOLUME: 'lineInfo',
	SNOWFLAKE_STORAGE_USAGE: 'lineInfo',
	SNOWFLAKE_COMPUTE_USAGE: 'lineInfo',

	DBT_RATES: 'lineSuccess',
	DBT_TEST_RESULTS: 'lineSuccess',
	DBT_TABLE_TEST_RESULTS: 'lineSuccess',

	MONITOR: 'lineSuccess',
	MONITOR_COUNT: 'lineInfo',
	INCIDENT_COUNT: 'lineInfo',

	AUTOMATION_RUN_COUNT: 'lineInfo',
	AUTOMATION_AFFECTED_RECORDS_COUNT: 'lineInfo',

	AI_CHAT_HISTORY: 'list',
	AI_CHAT_COUNT: 'lineInfo',

	BIGQUERY_COMPUTE_COST: 'lineInfo',
	BIGQUERY_STORAGE_COST: 'lineInfo',
};

interface ReportImageGridProps {
	widgets: IWidget[];
	reportType: string;
}

export default function ReportImageGrid({
	widgets,
	reportType,
}: ReportImageGridProps) {
	const { theme } = useStyles();

	if (reportType === 'data_quality') {
		return (
			<Box
				style={{
					height: '100%',
					display: 'flex',
					flexDirection: 'row',
					gap: theme.spacing.xs,
					overflow: 'hidden',
					borderRadius: theme.radius.sm,
				}}
			>
				<img
					src={IMAGES.dqs[theme.colorScheme]}
					alt="DQS"
					style={{
						width: '50%',
						height: '100%',
						objectFit: 'cover',
						borderRadius: theme.radius.sm,
					}}
				/>
				<img
					src={IMAGES.lineInfo[theme.colorScheme]}
					alt="Line Info"
					style={{
						width: '50%',
						height: '100%',
						objectFit: 'cover',
						borderRadius: theme.radius.sm,
					}}
				/>
			</Box>
		);
	}

	if (widgets.length === 0) {
		return (
			<img
				src={IMAGES.empty[theme.colorScheme]}
				alt="Empty"
				height="100px"
				width="100%"
				style={{ borderRadius: theme.radius.sm }}
			/>
		);
	}

	const ROW_SIZE = Number(WIDGET_SIZE_MAP[WidgetSize.FULL]);
	const widgetsToRender: typeof widgets = [];
	let totalSize = 0;
	let firstRowFilled = false;

	for (const widget of widgets) {
		const size = Number(WIDGET_SIZE_MAP[widget.size]);
		if (totalSize + size > ROW_SIZE && !firstRowFilled) {
			// first row is now filled
			firstRowFilled = true;
			widgetsToRender.push(widget);
			totalSize = size;
		} else if (totalSize + size > ROW_SIZE && firstRowFilled) {
			// second row is also filled
			break;
		} else {
			widgetsToRender.push(widget);
			totalSize += size;
		}
	}

	const moreThanOneRow =
		widgetsToRender.reduce(
			(acc, widget) => acc + Number(WIDGET_SIZE_MAP[widget.size]),
			0
		) > ROW_SIZE;

	return (
		<Box
			style={{
				height: '100%',
				display: 'grid',
				gridTemplateColumns: `repeat(${ROW_SIZE}, 1fr)`,
				gridTemplateRows: moreThanOneRow ? 'repeat(2, 1fr)' : 'repeat(1, 1fr)',
				gap: theme.spacing.xs,
				padding: 0,
			}}
		>
			{widgetsToRender.map((widget, idx) => (
				<Box
					key={`image-${idx}`} /* eslint-disable-line */
					style={{
						gridColumn: getWidgetSpan(widget.size),
						height: '100%',
						overflow: 'hidden',
						position: 'relative',
						borderRadius: theme.radius.sm,
					}}
				>
					<img
						src={
							IMAGES[
								IMAGE_MAP[widget.metric_metadata?.metric_name || 'TOP_USERS']
							][theme.colorScheme]
						}
						style={{
							borderRadius: theme.radius.sm,
							width: '100%',
							height: '100%',
							objectFit: 'cover',
							display: 'block',
						}}
					/>
				</Box>
			))}
		</Box>
	);
}
